import { useEffect, useMemo, useRef, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import * as z from 'zod';
import { SchemaSection } from '@rossum/api-client/schemas';
import { IconCode, IconSettings } from '@rossum/ui/icons/tabler';
import { Box, Button, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import Editor from '../../../../../../components/UI/Editor/components/Editor';
import { parseJsonPreprocessor } from '../../../../../../lib/parseJsonPreprocessor';
import { DatasetColumn } from '../../../../../datasets/types';
import { getSchemaFields } from '../../../../../formulas/FormulaEditor/helpers';
import { useAggregateCompleters } from './hooks/useAggregateCompleters';
import { useEditorOverlays } from './hooks/useEditorOverlays';
import {
  Placeholder,
  QueryObject,
  queryObjectSchema,
  VariablesMap,
} from './types';
import { Variables } from './Variables';

type AggregateEditorProps = {
  value: Partial<{
    queries: QueryObject[];
    variables: VariablesMap;
  }>;
  onChange: (value: {
    queries: QueryObject[];
    variables: VariablesMap;
  }) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  schemaContent: SchemaSection[];
  columns: (DatasetColumn & { samples?: string[] })[];
};

export const AggregateEditor = ({
  value,
  onChange,
  onFocus,
  onBlur,
  schemaContent,
  columns,
}: AggregateEditorProps) => {
  const intl = useIntl();
  const [placeholderMap, setPlaceholderMap] = useState(
    () => new Map<string, Placeholder>()
  );
  const [displayValue, setDisplayValue] = useState('');
  const [showVariables, setShowVariables] = useState<{
    open: boolean;
    variableToEdit: string | null;
  }>({
    open: false,
    variableToEdit: null,
  });
  const lastExternalQueriesRef = useRef<QueryObject[] | undefined>(undefined);

  const { editorRef, overlayContainerRef, overlayPositions, isScrolling } =
    useEditorOverlays(placeholderMap);

  const { onLoad: registerCompleters } = useAggregateCompleters({
    variables: value.variables ?? {},
    columns,
  });

  useEffect(() => {
    const placeholdersArray = value.variables
      ? Object.entries(value.variables).map(([key, variable]) => ({
          id: `$$${key}`,
          originalValue: variable.__formula,
          displayLabel: key,
        }))
      : [];

    setPlaceholderMap(new Map(placeholdersArray.map(p => [p.id, p])));
  }, [value.variables]);

  const parsedDisplayValue = useMemo(() => {
    return z
      .preprocess(parseJsonPreprocessor, z.array(queryObjectSchema))
      .safeParse(displayValue);
  }, [displayValue]);

  const isAggregateValid = useMemo(() => {
    return parsedDisplayValue.success;
  }, [parsedDisplayValue]);

  useEffect(() => {
    if (!R.isDeepEqual(value.queries, lastExternalQueriesRef.current)) {
      lastExternalQueriesRef.current = value.queries;
      setDisplayValue(JSON.stringify(value?.queries ?? [], null, 2));
    }
  }, [value.queries]);

  const handleEditorChange = (newValue: string) => {
    setDisplayValue(newValue);

    const parsedNewValue = z
      .preprocess(parseJsonPreprocessor, z.array(queryObjectSchema))
      .safeParse(newValue);

    if (parsedNewValue.success) {
      lastExternalQueriesRef.current = parsedNewValue.data;
      onChange({
        queries: parsedNewValue.data,
        variables: value?.variables ?? {},
      });
    }
  };

  const handleVariablesChange = (newVariables: VariablesMap) => {
    onChange({
      queries: value?.queries ?? [],
      variables: newVariables,
    });
  };

  const handleFormatJson = () => {
    if (parsedDisplayValue.success) {
      setDisplayValue(JSON.stringify(parsedDisplayValue.data, null, 2));
    }
  };

  const schemaFields = useMemo(
    () =>
      getSchemaFields(schemaContent).filter(
        ({ category }) => category === 'datapoint'
      ),
    [schemaContent]
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        alignItems="center"
      >
        <Button
          startIcon={
            <SvgIcon fontSize="large">
              <IconCode />
            </SvgIcon>
          }
          onClick={handleFormatJson}
          size="small"
          variant="outlined"
          disabled={!isAggregateValid}
          title={intl.formatMessage({
            id: 'features.queueSettings.customLookup.format',
          })}
        >
          {intl.formatMessage({
            id: 'features.queueSettings.customLookup.format',
          })}
        </Button>
        <Button
          startIcon={
            <SvgIcon fontSize="large">
              <IconSettings />
            </SvgIcon>
          }
          onClick={() => setShowVariables({ open: true, variableToEdit: null })}
          size="small"
          variant="outlined"
        >
          {intl.formatMessage({
            id: 'features.queueSettings.customLookup.variables',
          })}
        </Button>
      </Stack>
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            height: '600px',
          }}
        >
          <Editor
            forwardRef={editorRef}
            mode="json"
            value={displayValue}
            onChange={handleEditorChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onLoad={registerCompleters}
            name="aggregate-lookup-editor"
          />
        </Box>

        <Box
          ref={overlayContainerRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          {Array.from(placeholderMap.values()).map(placeholder => {
            const position = overlayPositions.get(placeholder.id);
            if (!position || !position.visible || isScrolling) return null;

            return (
              <Box
                key={placeholder.id}
                sx={{
                  position: 'absolute',
                  top: position.top,
                  left: position.left,
                  width: position.width,
                  height: position.height,
                  pointerEvents: 'auto',
                  zIndex: 20,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  },
                }}
                onClick={() =>
                  setShowVariables({
                    open: true,
                    variableToEdit: placeholder.displayLabel,
                  })
                }
              />
            );
          })}
        </Box>
      </Box>
      {isAggregateValid ? null : (
        <Typography variant="subtitle2" color="error">
          {intl.formatMessage({
            id: 'features.queueSettings.customLookup.invalidAggregate',
          })}
        </Typography>
      )}
      {showVariables.open && (
        <Variables
          open={showVariables.open}
          onClose={() =>
            setShowVariables({ open: false, variableToEdit: null })
          }
          variables={value.variables ?? {}}
          onChange={handleVariablesChange}
          queriesValue={displayValue}
          schemaFields={schemaFields}
          variableToEdit={showVariables.variableToEdit}
        />
      )}
    </Stack>
  );
};
